/**
 * Lecture d'une grille de programmation.
 *
 * Côté partagé, donc exécutable hors ligne : détecter qu'on a mis en favori deux
 * concerts qui se chevauchent ne doit pas dépendre du réseau.
 */

/** Regroupe les passages par journée festivalière, chaque groupe trié par heure. */
export function groupByFestivalDay<T extends { festivalDay: string; startsAt: string }>(
  performances: readonly T[],
): Array<{ day: string; performances: T[] }> {
  const byDay = new Map<string, T[]>();

  for (const performance of performances) {
    const bucket = byDay.get(performance.festivalDay);
    if (bucket) bucket.push(performance);
    else byDay.set(performance.festivalDay, [performance]);
  }

  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, items]) => ({
      day,
      performances: items.sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    }));
}

export type Conflict<T> = { a: T; b: T; overlapMinutes: number };

/**
 * Recherche les chevauchements dans une sélection de passages.
 *
 * Deux concerts qui se touchent bout à bout ne sont pas en conflit : finir à 21h
 * et commencer à 21h laisse le temps de traverser — c'est le cas le plus fréquent
 * d'une grille bien construite, le signaler serait du bruit.
 *
 * Les passages annulés sont ignorés : ils ne se disputent plus rien.
 */
export function findConflicts<
  T extends { id: string; startsAt: string; endsAt: string; status?: string },
>(performances: readonly T[]): Array<Conflict<T>> {
  const active = performances
    .filter((p) => p.status !== "CANCELLED")
    .slice()
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  const conflicts: Array<Conflict<T>> = [];

  for (let i = 0; i < active.length; i += 1) {
    for (let j = i + 1; j < active.length; j += 1) {
      const a = active[i] as T;
      const b = active[j] as T;

      const aEnd = Date.parse(a.endsAt);
      const bStart = Date.parse(b.startsAt);

      // La liste étant triée par début, dès qu'un passage commence après la fin
      // de `a`, tous les suivants aussi : inutile de poursuivre.
      if (bStart >= aEnd) break;

      const overlapMs = Math.min(aEnd, Date.parse(b.endsAt)) - bStart;
      if (overlapMs > 0) {
        conflicts.push({ a, b, overlapMinutes: Math.round(overlapMs / 60_000) });
      }
    }
  }

  return conflicts;
}
